import React, {useRef, useEffect, useState, useCallback, useMemo} from 'react';
import {Box} from '@mui/material';

interface AurBarStripeProps {
  height?: number;
  gap?: number;
  stripeWidth?: number;
  percent?: number; // 0-100
  state?: 'in-progress' | 'in-queue' | 'failed' | 'completed';
}

const FIXED_WIDTH = 300;
const TOTAL_STRIPES = 100;

const AurBarStripe: React.FC<AurBarStripeProps> = ({
  height = 24,
  gap = 0.8,
  stripeWidth = 2,
  percent = 0,
  state = 'in-queue',
}) => {
  const containerRef = useRef<HTMLDivElement>(null);

  const [stripeWidthPx, setStripeWidthPx] = useState<number>(stripeWidth);
  const [gapPx, setGapPx] = useState<number>(gap);

  const calculateStripes = useCallback(() => {
    const baseStripe = stripeWidth > 0 ? stripeWidth : 1;
    const baseGap = gap >= 0 ? gap : 0;

    if (baseGap > 0) {
      const ratio = baseStripe / baseGap;
      const calculatedGap =
        FIXED_WIDTH / (TOTAL_STRIPES * ratio + (TOTAL_STRIPES - 1));
      const calculatedStripeWidth = calculatedGap * ratio;

      setStripeWidthPx(calculatedStripeWidth);
      setGapPx(calculatedGap);
    } else {
      setStripeWidthPx(FIXED_WIDTH / TOTAL_STRIPES);
      setGapPx(0);
    }
  }, [gap, stripeWidth]);

  useEffect(() => {
    calculateStripes();
  }, [calculateStripes, gap, stripeWidth]);

  const safePercent = useMemo(
    () => Math.max(0, Math.min(100, Math.round(percent))),
    [percent]
  );

  const actualStripeWidth = useMemo(() => stripeWidthPx, [stripeWidthPx]);

  const actualGap = useMemo(() => (gapPx > 0 ? gapPx : 0.8), [gapPx]);

  const oneUnitPx = useMemo(
    () => actualStripeWidth + actualGap,
    [actualGap, actualStripeWidth]
  );

  const stripePercent = useMemo(
    () => (oneUnitPx > 0 ? (actualStripeWidth / oneUnitPx) * 100 : 0),
    [actualStripeWidth, oneUnitPx]
  );

  const stripePattern = useMemo(
    () => `repeating-linear-gradient(
    90deg,
    black 0,
    black ${stripePercent.toFixed(6)}%,
    transparent ${stripePercent.toFixed(6)}%,
    transparent 100%
  )`,
    [stripePercent]
  );

  // State color logic
  const isInProgress = useMemo(() => state === 'in-progress', [state]);

  const isFailed = useMemo(() => state === 'failed', [state]);

  const isCompleted = useMemo(() => state === 'completed', [state]);

  const remainingColor = useMemo(() => 'var(--surface-bold, #2D3D55)', []);

  const completedColorStyle = useMemo(() => {
    if (isInProgress)
      return {
        backgroundImage:
          'linear-gradient(90deg, #008D95 -22.17%, #5265FF 49.38%, #764CB6 120.94%)',
      };

    if (isFailed) return {background: 'var(--icon-critical, #ff7a7a)'};

    if (isCompleted) return {background: 'var(--pfp-green-dark, #7DDFBB)'};

    return {background: remainingColor};
  }, [isCompleted, isFailed, isInProgress, remainingColor]);

  return (
    <Box
      ref={containerRef}
      position="relative"
      width="300px"
      height={`${height}px`}
      sx={{
        display: 'flex',
        background: 'transparent',
      }}
      data-testid={'aur-bar-stripe'}
    >
      {/* Completed */}
      <Box
        sx={{
          position: 'absolute',
          left: 0,
          top: 0,
          width: `${safePercent}%`,
          height: '100%',
          ...completedColorStyle,
          maskImage: stripePattern,
          WebkitMaskImage: stripePattern,
          maskSize: `${oneUnitPx.toFixed(3)}px 100%`,
          WebkitMaskSize: `${oneUnitPx.toFixed(3)}px 100%`,
          maskRepeat: 'repeat-x',
          WebkitMaskRepeat: 'repeat-x',
        }}
      />

      {/* Remaining */}
      <Box
        sx={{
          position: 'absolute',
          left: `${safePercent}%`,
          top: 0,
          right: 0,
          height: '100%',
          background: remainingColor,
          maskImage: stripePattern,
          WebkitMaskImage: stripePattern,
          maskSize: `${oneUnitPx.toFixed(3)}px 100%`,
          WebkitMaskSize: `${oneUnitPx.toFixed(3)}px 100%`,
          maskRepeat: 'repeat-x',
          WebkitMaskRepeat: 'repeat-x',
          maskPosition: `-${safePercent * oneUnitPx}px 0`,
          WebkitMaskPosition: `-${safePercent * oneUnitPx}px 0`,
        }}
      />
    </Box>
  );
};

export default AurBarStripe;
